import type { TechDataVehicle } from '@/types/types';
import { getEnvData } from './../utils/aem';
import type { PageEnvironmentVariables } from '@seamless/one-context';
import type { TrackingEvent } from '@owc/connection-aem-tracking';

/**
 * Composable for tracking various user interactions in the Tech Data component.
 * @param modelSeries - The model series code for the vehicle.
 */

declare const __APP_VERSION__: string;

type TrackedVehicle = {
  modelCode: string;
  class: string;
  bodyType: string;
  baumusterId: string;
  type: 'van';
  category: 'commercial';
};

type EventDetail = {
  name: string;
  category: string;
  action: string;
  label: string;
  vehicle: TrackedVehicle;
};

type TrackingDispatchers = {
  pushTrackingEvent: (evt: TrackingEvent) => void;
};

let envPromise: Promise<PageEnvironmentVariables | null> | null = null;
let dispatchersPromise: Promise<TrackingDispatchers | null> | null = null;

function isBrowser() {
  return typeof window !== 'undefined';
}

async function getEnv(): Promise<PageEnvironmentVariables | null> {
  if (!envPromise) {
    envPromise = getEnvData()
      .then((response) => response.pageEnvironmentVariables ?? null)
      .catch(() => null);
  }
  return envPromise;
}

async function getDispatchers(
  env?: PageEnvironmentVariables | null,
): Promise<TrackingDispatchers | null> {
  const resolvedEnv = env ?? (await getEnv());
  if (!resolvedEnv || resolvedEnv.stage !== 'PROD') return null;

  if (!dispatchersPromise) {
    dispatchersPromise = import('@owc/connection-aem-tracking')
      .then(
        (mod) => mod.getTrackingDispatchers() as unknown as TrackingDispatchers,
      )
      .catch(() => null);
  }

  return dispatchersPromise;
}

function createDataLayerEvent(eventDetail: EventDetail): TrackingEvent {
  return {
    event: 'click',
    eventDetail,
    application: {
      id: 'cvans',
      version: __APP_VERSION__,
      businessUnit: 'b2b',
    },
  };
}

async function pushDataLayerEvent(
  eventDetail: EventDetail,
  env?: PageEnvironmentVariables | null,
): Promise<void> {
  if (!isBrowser()) return;

  const dispatchers = await getDispatchers(env);
  if (!dispatchers) return;

  const evt = createDataLayerEvent(eventDetail);

  window.dataLayer_ow ||= [];

  const alreadyExists = window.dataLayer_ow.some(
    (existing) => JSON.stringify(existing) === JSON.stringify(evt),
  );

  if (!alreadyExists) {
    window.dataLayer_ow.push(evt);
  }

  try {
    dispatchers.pushTrackingEvent(evt);
  } catch (e) {
    console.error('Failed to push tracking event:', e);
  }
}

export function useTracking(modelSeries: string) {
  const buildVehicleObj = (vehicle: TechDataVehicle): TrackedVehicle => ({
    modelCode: modelSeries,
    class: vehicle.vehicleClass,
    bodyType: vehicle.bodyType,
    baumusterId: vehicle.baumuster,
    type: 'van',
    category: 'commercial',
  });

  const trackEvent = (
    name: string,
    category: string,
    action: string,
    label: string,
    vehicle: TechDataVehicle | null,
  ) => {
    if (!vehicle) {
      console.warn('No vehicle selected for tracking.');
      return;
    }

    const eventData: EventDetail = {
      name,
      category,
      action,
      label,
      vehicle: buildVehicleObj(vehicle),
    };

    getEnv()
      .then((env) => {
        if (!env || env.stage !== 'PROD') {
          console.groupCollapsed(
            '%cOWVANS Tracking Event',
            'color:#00A3E0;font-weight:600;',
          );

          console.table({
            Name: eventData.name,
            Category: eventData.category,
            Action: eventData.action,
            Label: eventData.label,
            Model: eventData.vehicle.modelCode,
            Class: eventData.vehicle.class,
            BodyType: eventData.vehicle.bodyType,
            Baumuster: eventData.vehicle.baumusterId,
          });

          console.groupEnd();
        }
        return pushDataLayerEvent(eventData, env);
      })
      .catch((e) => console.error('Tracking failed:', e));
  };

  return {
    trackFeatureSelection: (label: string, v: TechDataVehicle | null) =>
      trackEvent(
        'feature',
        'feature',
        'cvans_techdata_component',
        label.trim().toLowerCase(),
        v,
      ),

    trackImpression: (v: TechDataVehicle | null) =>
      trackEvent(
        'impression',
        'impression',
        'cvans_techdata_component',
        'cvans_techdata',
        v,
      ),

    trackModalButton: (label: string, v: TechDataVehicle | null) =>
      trackEvent(
        'open_modal',
        'feature',
        'cvans_techdata_component',
        label.trim().toLowerCase(),
        v,
      ),

    trackModalScroll: (label: string, v: TechDataVehicle | null) =>
      trackEvent(
        'impression',
        'impression',
        'cvans_techdata_component',
        label.trim().toLowerCase(),
        v,
      ),
  };
}
